import { logger } from '@/lib/logger';
/**
 * Global Audio Manager with persistent AudioContext
 * Handles browser autoplay policies by unlocking audio during user interaction
 */

/**
 * Synthesizes and plays a pleasant bell chime "plim" sound
 */
const playBellChime = async (volume: number = 0.3): Promise<void> => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') await ctx.resume();
    
    const now = ctx.currentTime;
    
    // Bell harmonics - creates a pleasant "plim" sound
    const frequencies = [
      { freq: 830, gain: 0.4 },   // G#5 - fundamental
      { freq: 1245, gain: 0.3 },  // D#6 - 5th harmonic  
      { freq: 1660, gain: 0.2 },  // G#6 - octave
      { freq: 2490, gain: 0.1 },  // D#7 - high shimmer
    ];
    
    frequencies.forEach(({ freq, gain: freqGain }) => {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      
      // Bell envelope: instant attack, exponential decay
      const amplitude = volume * freqGain;
      gainNode.gain.setValueAtTime(amplitude, now);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
      
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      osc.start(now);
      osc.stop(now + 0.6);
    });
  } catch (e) {
    logger.log('Bell chime failed:', e);
  }
};

// Pomodoro completion sound - different tone (WAV format)
const POMODORO_SOUND_BASE64 = 'data:audio/wav;base64,UklGRrQEAABXQVZFZm10IBAAAAABAAEAIlYAAHCoAAABAAgAZGF0YZAEAAB/f39/f39/f39/f3+AgIGChIWHiYuNj5GSk5SVlpaWlpWUk5GPjYuJh4WDgoB/fn18e3t7e3x9f4CCg4aIioyOkJKTlJWWlpaWlZSTkY+NioiFg4KAf317env7+/v7/Pz9/oB/gIGChIaIi42QkpSVlpaWlpWUkpCOjImHhYOBf358e3t7e3x9f4CCg4aIioyOkJKTlJWWlpaWlZSTkY+NioiFg4KAf357env7+/v7/Pz9/oB/gIGChIaIi42QkpSVlpaWlpWUkpCOjImHhYOBf358e3t7e3x9f4CCg4aIioyOkJKTlJWWlpaWlZSTkY+NioiFg4KAf357env7+/v7/Pz9/oB/gIGChIaIi42QkpSVlpaWlpWUkpCOjImHhYOBf358e3t7e3x9f4CCg4aIioyOkJKTlJWWlpaWlZSTkY+NioiFg4KAf357e3t7e3x9fn+AgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/fn18fHx8fH1+f4CAgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/fn18fHx8fH1+f4CAgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/fn18fHx8fH1+f4CAgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/fn18fHx8fH1+f4CAgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/fn18fHx8fH1+f4CAgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/fn18fHx8fH1+f4CAgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/fn18fHx8fH1+f4CAgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/fn18fHx8fH1+f4CAgYOFh4mLjY+RkpOUlZaWlpWUk5KQjo2KiIaEgoB/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/';

// Extend Window interface for webkit prefix
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

// Singleton AudioContext that persists during the session
let audioContext: AudioContext | null = null;
let isUnlocked = false;

/**
 * Gets or creates the singleton AudioContext
 */
export const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    audioContext = new AudioContextClass();
  }
  return audioContext;
};

/**
 * Unlocks the AudioContext - MUST be called during a user interaction (click, touch)
 * This satisfies browser autoplay policies
 */
export const unlockAudioContext = async (): Promise<boolean> => {
  try {
    const ctx = getAudioContext();
    
    // Resume if suspended
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    
    // Play a silent oscillator to fully unlock (some browsers require this)
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0; // Silent
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.001);
    
    isUnlocked = true;
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Check if audio is enabled in user preferences
 */
export const isAudioEnabled = (): boolean => {
  return localStorage.getItem('notification-sound-enabled') === 'true';
};

/**
 * Plays a sound from base64 data using the AudioContext
 */
const playSoundFromBase64 = async (base64Data: string, volume: number = 0.5): Promise<void> => {
  try {
    const ctx = getAudioContext();
    
    // Try to resume if suspended (may work if we're in a user gesture)
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    
    // Decode base64 to ArrayBuffer
    const base64 = base64Data.split(',')[1];
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Decode audio data
    const audioBuffer = await ctx.decodeAudioData(bytes.buffer);
    
    // Create source and gain nodes
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    
    const gainNode = ctx.createGain();
    gainNode.gain.value = volume;
    
    source.connect(gainNode);
    gainNode.connect(ctx.destination);
    source.start();
  } catch (e) {
    // Fallback to Audio element
    try {
      const audio = new Audio(base64Data);
      audio.volume = volume;
      await audio.play();
    } catch {
      // Silent fail - audio is not critical
    }
  }
};

/**
 * Plays the notification sound (for new messages, alerts)
 * Respects user preference in localStorage
 */
export const playNotificationSound = async (): Promise<void> => {
  if (!isAudioEnabled()) return;
  await playBellChime(0.3);
};

/**
 * Plays the Pomodoro completion sound
 * Takes explicit enabled parameter since ModoForja has its own toggle
 */
export const playPomodoroSound = async (enabled: boolean = true): Promise<void> => {
  if (!enabled) return;
  await playSoundFromBase64(POMODORO_SOUND_BASE64, 0.3);
};

/**
 * Check if AudioContext is currently unlocked
 */
export const isAudioUnlocked = (): boolean => {
  return isUnlocked && audioContext?.state === 'running';
};
